// lib
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { plural } from '@aytm/app/stats/lib/helpers'
import useEventListener from '@aytm/app/stats/hooks/useEventListener'

import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'
import { getQuestionId, getQuestionIcon } from '@aytm/app/stats/page/components/lib/utils'

// components
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { Checkbox } from 'primereact/checkbox'
import { Slider } from 'primereact/slider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/crosstab/styles/CrosstabPanel.module.scss'

const getBannerKey = question => {
  const id = getQuestionId(question)

  const type = question.variant || question.type
  const key = `${id}${'psc' == type ? 'psc' : ''}`

  return 'dlx' == question.type
    ? Object.hasOwn(question, 'batches')
      ? `${key}:${question.batches.map(b => `sq${b.position}`).join('-')}`
      : `${key}:${question.answerVariants.map(av => `a${av.position}`).join('-')}`
    : key
}

const DATA_FORMAT_HELP =
  'Choose to show counts, percentages, or both. If "percentages" or "both" are selected, customize the rounding to just the whole number, or up to 10 decimal places. This setting will apply to all question types in your banner export, including Concept Lab reports, TURF analyses, and more.'

const SIGNIFICANCE_TESTING_HELP =
  "Confidence level determines how certain a researcher can be regarding their sample accurately revealing the attitudes of the total population being tested. Primary CL, usually set to 95% for market research purposes, is used for a variety of significance tests on this page and in statistical reports. In Banner Table reports, Primary CL designates statistically significant difference at 95% and is marked with upper case letters. Secondary CL defaults to 90% and is signified with lower case letters. Total only tests each banner column against the overall Total. Within banners only tests columns pairwise within each banner group. Total and within banners applies both. <br/><br/> <a href='https://helpcenter.aytm.com/hc/en-us/articles/360059216031-Significance-Testing-with-aytm' target='_blank'>Learn more about Significance Testing with aytm</a>"

const CrosstabPanel = ({
  root,
  model,
  data,
  disabled,
  onBannersSelect,
  onLevelsChange,
  onPercentDecimalsChange,
  onExport
}) => {
  const { totalNumberOfConnections } = data

  const { responses, totalRespondents } = model
  const { traits, variables, questions, topsModes, levels, unlockNumber } = data

  const [banners, setBanners] = useState([])

  const [showCounts, setShowCounts] = useState(true)
  const [showPercents, setShowPercents] = useState(true)

  const [confidenceLevel, setConfidenceLevel] = useState(model.confidenceLevel || 0.95)
  const [significanceLevel, setSignificanceLevel] = useState(model.significanceLevel || 0.9)
  const [percentDecimals, setPercentDecimals] = useState(model.percentDecimals)

  const [showTesting, setShowTesting] = useState(true)
  const [sigTestMode, setSigTestMode] = useState('all')

  const [top2Box, setTop2Box] = useState(false)
  const [top3Box, setTop3Box] = useState(false)
  const [top15Box, setTop15Box] = useState(false)

  const [showMean, setShowMean] = useState(false)

  const [failed, setFailed] = useState(false)
  const [processing, setProcessing] = useState(false)

  const timer = useRef(null)

  const optionsRef = useRef(null)
  const optionsScrollTopRef = useRef(null)

  const bannersRef = useRef(null)
  bannersRef.current = banners

  const extra = useRef({})
  extra.current = {
    confidenceLevel,
    significanceLevel,
    percentDecimals,
    showCounts,
    showPercents,
    showTesting,
    showPValues: true,
    ...(showTesting && { sigTestMode }),
    top15Box,
    top2Box,
    top3Box,
    showMean
  }

  const bannerOptions = useMemo(
    () =>
      traits
        .reduce(
          (acc, trait) =>
            banners.some(banner => banner.key == trait.id)
              ? acc
              : acc.concat([{ key: trait.id, text: trait.title, value: trait, type: 'trait' }]),
          []
        )
        .concat(
          variables.reduce(
            (acc, variable) =>
              banners.some(banner => banner.key == variable.key)
                ? acc
                : acc.concat([{ key: variable.key, text: variable.key, value: variable, type: 'variable' }]),
            []
          )
        )
        .concat(
          questions.reduce((acc, question) => {
            const id = getQuestionId(question)
            const key = getBannerKey(question)

            const text = `${id.toUpperCase()}: ${question.text}`

            const icon = getQuestionIcon(question)

            return banners.some(banner => banner.key == key)
              ? acc
              : acc.concat([{ key, text, icon, value: question, type: 'question' }])
          }, [])
        ),
    [traits, variables, questions, banners]
  )

  const overLimit = useMemo(() => {
    const numberOfConnections = banners.reduce((amount, banner) => {
      if (['trait', 'variable'].includes(banner.type)) {
        return amount + banner.value.values.length || 0
      }

      if ('question' == banner.type) {
        const question = banner.value

        const type = question.variant || question.type

        if (['rb', 'cb', 'scx', 'sft'].includes(type))
          return amount + (question.answerVariants.length * (question.answerVariants.length - 1)) / 2
        if (['mx', 'se', 'sr', 'mp'].includes(type))
          return (
            amount +
            (question.batches.length *
              question.batches[0].answerVariants.length *
              (question.batches[0].answerVariants.length - 1)) /
              2
          )
        if (['qa', 'bpm'].includes(type))
          return (
            amount + (question.questionVariants.length * question.batches.length * (question.batches.length - 1)) / 2
          )
        if (['ro', 'aro'].includes(type))
          return amount + (question.batches.length * question.batches.length * (question.batches.length - 1)) / 2
        if (['psc'].includes(type)) return amount + 3 * question.batches.length
      }

      return amount
    }, 0)

    return totalNumberOfConnections <= numberOfConnections
  }, [totalNumberOfConnections, banners])

  const exportDisabled = useMemo(
    () => responses < unlockNumber || totalRespondents < unlockNumber || !questions.length || disabled || overLimit,
    [responses, totalRespondents, questions, unlockNumber, disabled, overLimit]
  )

  const crossByLogic = useMemo(() => {
    const option = bannerOptions.find(banner => 'question' == banner.type)

    return ((0 < banners.length && banners) || (option != undefined && [option]) || []).reduce((acc, banner) => {
      if (['trait', 'variable'].includes(banner.type)) {
        acc.push(banner.key)
      }

      if ('question' == banner.type) {
        const question = banner.value

        const key =
          0 < question.position
            ? `${(question.pre_type && 'PQ') || 'Q'}${question.visual_position}`
            : question.id.toUpperCase()
        const type = question.variant || question.type

        if (Object.hasOwn(question, 'questionVariants') && Object.hasOwn(question, 'batches')) {
          for (const qv of question.questionVariants) {
            for (const b of question.batches) {
              acc.push(`${key}E${qv.position}SQ${b.position}`)
            }
          }
        } else if (Object.hasOwn(question, 'batches')) {
          for (const b of question.batches) {
            acc.push(`${key}${['ro', 'aro'].includes(type) ? 'A' : 'SQ'}${b.position}`)
          }
        } else acc.push(`${key}`)
      }

      return acc
    }, [])
  }, [banners, bannerOptions])

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className={option.item ? '' : 'p-item-label'}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon && <i className={`p-item-icon icon-aytmfd-${option.icon}`} />}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const onDataFormatChange = (counts, percents) => {
    setShowCounts(counts)
    setShowPercents(percents)

    setPercentDecimals(percents ? 1 : null)
  }

  const onBannerHide = () => {
    const overlay = optionsRef.current.getOverlay()

    const wrapper = overlay.querySelector('.p-dropdown-items-wrapper')
    optionsScrollTopRef.current = wrapper.scrollTop
  }

  const onBannerShow = useCallback(() => {
    const overlay = optionsRef.current.getOverlay()

    const wrapper = overlay.querySelector('.p-dropdown-items-wrapper')
    wrapper.scrollTop = optionsScrollTopRef.current

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      overlay.classList.add('p-rendered')
    })

    return () => timer.current && clearTimeout(timer.current)
  }, [])

  const onBannerSelect = useCallback(
    value => {
      const option = bannerOptions.find(_option => _option.key == value)
      setBanners(banners.concat([option]))
    },
    [banners, bannerOptions]
  )

  const onBannerRemove = useCallback(value => setBanners(banners.filter(_banner => _banner.key != value)), [banners])

  const onShowTestingToggle = useCallback(() => {
    setShowTesting(value => !value)
    setSigTestMode('all') // toggling significance testing resets the comparison mode to its default
  }, [])

  const onClick = useCallback(() => {
    setFailed(false)
    setProcessing(true)

    onExport(crossByLogic, extra.current)
  }, [crossByLogic, onExport])

  useEffect(
    () => {
      optionsScrollTopRef.current = 0
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [traits, variables, questions]
  )

  useEffect(
    () => onLevelsChange(confidenceLevel, Math.min(extra.current.significanceLevel, confidenceLevel)),
    [confidenceLevel, onLevelsChange]
  )
  useEffect(
    () => onLevelsChange(Math.max(extra.current.confidenceLevel, significanceLevel), significanceLevel),
    [significanceLevel, onLevelsChange]
  )

  useEffect(() => onPercentDecimalsChange(percentDecimals), [percentDecimals, onPercentDecimalsChange])

  useEffect(() => {
    if (disabled) setBanners([])
    else {
      setBanners(
        bannersRef.current
          .map(banner => {
            if ('trait' == banner.type) {
              const trait = traits.find(_trait => _trait.id == banner.key)
              if (trait != undefined) {
                banner.value = trait
                return banner
              }
            }
            if ('variable' == banner.type) {
              const variable = variables.find(_variable => _variable.key == banner.key)
              if (variable != undefined) {
                banner.value = variable
                return banner
              }
            }
            if ('question' == banner.type) {
              const question = questions.find(_question => getBannerKey(_question) == banner.key)
              if (question != undefined) {
                banner.value = question
                return banner
              }
            }

            return null
          })
          .filter(banner => null !== banner)
      )
    }
  }, [traits, variables, questions, disabled])

  useEffect(() => {
    if (disabled) {
      setTop2Box(false)
      setTop3Box(false)
      setTop15Box(false)
      setShowMean(false)
    } else {
      const [_top2Box, _top3Box, _top15Box, _showMean] = questions.reduce(
        (acc, question) => {
          const topsMode = topsModes[(0 < question.position && question.position) || question.id]
          return [
            acc[0] || ['top2', 'bottom2'].includes(topsMode) || false,
            acc[1] || ['top3', 'bottom3'].includes(topsMode) || false,
            acc[2] || ['top2w', 'bottom2w'].includes(topsMode) || false,
            acc[3] || ['means', 'meansReversed'].includes(topsMode) || false
          ]
        },
        [false, false, false, false]
      )

      setTop2Box(_top2Box)
      setTop3Box(_top3Box)
      setTop15Box(_top15Box)
      setShowMean(_showMean)
    }
  }, [questions, topsModes, disabled])

  useEffect(() => {
    setConfidenceLevel(model.confidenceLevel)
    setSignificanceLevel(model.significanceLevel)
  }, [model.confidenceLevel, model.significanceLevel])

  useEffect(() => {
    if (extra.current.percentDecimals === model.percentDecimals) return

    if (null !== model.percentDecimals) {
      setShowCounts(true)
      setShowPercents(true)
    } else {
      setShowCounts(true)
      setShowPercents(false)
    }

    setPercentDecimals(0 <= model.percentDecimals ? model.percentDecimals : null)
  }, [model.percentDecimals])

  useEffect(() => onBannersSelect(banners, crossByLogic), [banners, crossByLogic, onBannersSelect])

  useEventListener(`crosstabExportSuccess`, () => setProcessing(false))

  useEventListener(`crosstabExportFailed`, () => {
    setFailed(true)
    setProcessing(false)
  })

  const renderBanner = banner => (
    <div className={style.button}>
      <span className={style['button-delete']} onClick={() => onBannerRemove(banner.key)}>
        <i className="icon-aytmfd-delete" />
      </span>
      {banner.icon ? <i className={`icon-aytmfd-${banner.icon}`} /> : null}
      <span className={style['button-text']}>
        {'question' == banner.type ? getQuestionId(banner.value).toUpperCase() : banner.text}
      </span>
    </div>
  )

  const renderBannersGroup = () => (
    <div className={style.section}>
      <div className={`${style.row} ${style['section-title']} p-fluid`}>
        <b>Banner Groups</b>
        <SidebarPopup
          root={root}
          help="Choose subgroups or categories for your analysis. Selected subgroups will make up the headers in your banner tables export. <br/><br/> <a href='https://helpcenter.aytm.com/hc/en-us/articles/17200144374029-Banner-Tables' target='_blank'>Learn more</a>"
          width="300px"
        />
      </div>
      {banners.map(banner => (
        <div key={banner.key} className={`${style.row} p-fluid`}>
          {renderBanner(banner)}
        </div>
      ))}
      <div className={`${style.row} p-fluid`}>
        <div className="p-field p-col-12">
          <Dropdown
            ref={optionsRef}
            className={style.options}
            id="crosstab-panel-add-banner-dropdown"
            placeholder="Add banner"
            value={null}
            options={bannerOptions}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            filter={true}
            filterBy="search,text"
            filterPlaceholder="Search items"
            resetFilterOnHide={true}
            filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            confirmKeyboardSelect
            disabled={disabled || overLimit}
            onChange={disabled || overLimit ? null : event => onBannerSelect(event.value)}
            onShow={disabled || overLimit ? null : onBannerShow}
            onHide={disabled || overLimit ? null : onBannerHide}
          />
        </div>
      </div>
    </div>
  )

  const renderDataFormat = () => (
    <div className={style.section}>
      <div className={`${style.row} ${style['section-title']} p-fluid`}>
        <b>Data Format</b>
        <SidebarPopup root={root} help={DATA_FORMAT_HELP} width="300px" />
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <RadioButton
          data-testid="stat-crosstab-show-counts"
          value="showCounts"
          checked={showCounts && !showPercents}
          disabled={disabled}
          onChange={disabled ? null : () => onDataFormatChange(true, false)}
        />
        <span
          data-testid="stat-crosstab-show-counts-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => onDataFormatChange(true, false)}
        >
          Show counts
        </span>
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <RadioButton
          data-testid="stat-crosstab-show-percentages"
          value="showPercents"
          checked={!showCounts && showPercents}
          disabled={disabled}
          onChange={disabled ? null : () => onDataFormatChange(false, true)}
        />
        <span
          data-testid="stat-crosstab-show-percentages-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => onDataFormatChange(false, true)}
        >
          Show percentages
        </span>
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <RadioButton
          data-testid="stat-crosstab-show-both"
          value="showBoth"
          checked={showCounts && showPercents}
          disabled={disabled}
          onChange={disabled ? null : () => onDataFormatChange(true, true)}
        />
        <span
          data-testid="stat-crosstab-show-both-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => onDataFormatChange(true, true)}
        >
          Show both
        </span>
      </div>
      {showPercents ? (
        <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
          <Checkbox
            data-testid="stat-crosstab-dataRounding"
            value="dataRounding"
            checked={null !== percentDecimals}
            disabled={disabled}
            onChange={disabled ? null : () => setPercentDecimals(null === percentDecimals ? 1 : null)}
            onKeyDown={
              disabled
                ? null
                : event => toggleCheckbox(event, () => setPercentDecimals(null === percentDecimals ? 1 : null))
            }
          />
          <span
            data-testid="stat-crosstab-dataRounding-label"
            className={classNames(style['row-label'], { 'p-disabled': disabled })}
            onClick={disabled ? null : () => setPercentDecimals(null === percentDecimals ? 1 : null)}
          >
            Percentage decimal place
          </span>
          {null === percentDecimals ? null : (
            <div className="p-field p-col-3">
              <InputNumber
                data-testid="stat-crosstab-percentDecimals"
                inputClassName="p-inputtext-rounded"
                value={percentDecimals}
                min={0}
                max={3}
                step={1}
                showButtons
                allowEmpty={false}
                disabled={disabled}
                onValueChange={disabled ? null : event => setPercentDecimals(null === event.value ? 1 : event.value)}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )

  const renderSignificanceTesting = () => (
    <>
      <div className={style.section}>
        <div className={`${style.row} ${style['section-title']} p-fluid`}>
          <b>Significance Testing</b>
          <SidebarPopup root={root} help={SIGNIFICANCE_TESTING_HELP} width="300px" />
        </div>
        <div className={`${style.row} ${style['row-slider']} p-fluid`}>
          <div className="p-field p-col-12">
            <Slider
              id="crosstab-panel-confidence-level-slider"
              min={0}
              max={levels.length - 1}
              value={levels.findIndex(v => v[0] == confidenceLevel)}
              labelTemplate={value => levels[value][1]}
              disabled={disabled}
              onChange={disabled ? null : event => setConfidenceLevel(levels[event.value][0])}
            />
          </div>
        </div>
        <div className={`${style.row} ${style['row-slider-label']} p-fluid`}>
          <span>Confidence level</span>
        </div>
        <div className={`${style.row} ${style['row-slider']} p-fluid`}>
          <div className="p-field p-col-12">
            <Slider
              id="crosstab-panel-significance-level-slider"
              min={0}
              max={levels.length - 1}
              value={levels.findIndex(v => v[0] == significanceLevel)}
              labelTemplate={value => levels[value][1]}
              disabled={disabled}
              onChange={disabled ? null : event => setSignificanceLevel(levels[event.value][0])}
            />
          </div>
        </div>
        <div className={`${style.row} ${style['row-slider-label']} p-fluid`}>
          <span>Secondary Confidence level</span>
        </div>
      </div>
      <div className={style.section}>
        <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
          <Checkbox
            data-testid="stat-crosstab-show-significance"
            value="testing"
            checked={showTesting}
            disabled={disabled}
            onChange={disabled ? null : onShowTestingToggle}
            onKeyDown={disabled ? null : event => toggleCheckbox(event, onShowTestingToggle)}
          />
          <span
            data-testid="stat-crosstab-show-significance-label"
            className={classNames(style['row-label'], { 'p-disabled': disabled })}
            onClick={disabled ? null : onShowTestingToggle}
          >
            Show significance testing
          </span>
        </div>
        {showTesting ? (
          <>
            <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
              <RadioButton
                data-testid="stat-crosstab-sigtest-totals"
                value="totals"
                checked={sigTestMode == 'totals'}
                disabled={disabled}
                onChange={disabled ? null : () => setSigTestMode('totals')}
              />
              <span
                data-testid="stat-crosstab-sigtest-totals-label"
                className={classNames(style['row-label'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setSigTestMode('totals')}
              >
                Compare against total only
              </span>
            </div>
            <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
              <RadioButton
                data-testid="stat-crosstab-sigtest-banners"
                value="banners"
                checked={sigTestMode == 'banners'}
                disabled={disabled}
                onChange={disabled ? null : () => setSigTestMode('banners')}
              />
              <span
                data-testid="stat-crosstab-sigtest-banners-label"
                className={classNames(style['row-label'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setSigTestMode('banners')}
              >
                Compare within banners only
              </span>
            </div>
            <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
              <RadioButton
                data-testid="stat-crosstab-sigtest-all"
                value="all"
                checked={sigTestMode == 'all'}
                disabled={disabled}
                onChange={disabled ? null : () => setSigTestMode('all')}
              />
              <span
                data-testid="stat-crosstab-sigtest-all-label"
                className={classNames(style['row-label'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setSigTestMode('all')}
              >
                Compare against total and within banners
              </span>
            </div>
          </>
        ) : null}
      </div>
    </>
  )

  const renderSummaryMetrics = () => (
    <div className={style.section}>
      <div className={`${style.row} ${style['section-title']} p-fluid`}>
        <b>Summary Metrics</b>
        <SidebarPopup
          root={root}
          help="Select box scores or metrics to include in your export. Summary metrics will automatically be applied to all applicable question types: radio buttons, progressive matrix, sliders (matrix), and star/smiley rating. <br/><br/> <a href='https://helpcenter.aytm.com/hc/en-us/articles/17200144374029-Banner-Tables' target='_blank'>Learn more about Banner Tables</a>"
          width="300px"
        />
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <Checkbox
          data-testid="stat-crosstab-top2box"
          value="top2box"
          checked={top2Box}
          disabled={disabled}
          onChange={disabled ? null : () => setTop2Box(!top2Box)}
          onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setTop2Box(!top2Box))}
        />
        <span
          data-testid="stat-crosstab-top2box-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => setTop2Box(!top2Box)}
        >
          Top and Bottom 2 Box
        </span>
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <Checkbox
          data-testid="stat-crosstab-top3box"
          value="top3box"
          checked={top3Box}
          disabled={disabled}
          onChange={disabled ? null : () => setTop3Box(!top3Box)}
          onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setTop3Box(!top3Box))}
        />
        <span
          data-testid="stat-crosstab-top3box-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => setTop3Box(!top3Box)}
        >
          Top and Bottom 3 Box
        </span>
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <Checkbox
          data-testid="stat-crosstab-top15box"
          value="top15box"
          checked={top15Box}
          disabled={disabled}
          onChange={disabled ? null : () => setTop15Box(!top15Box)}
          onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setTop15Box(!top15Box))}
        />
        <span
          data-testid="stat-crosstab-top15box-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => setTop15Box(!top15Box)}
        >
          Top and Bottom 1.5 Box
        </span>
      </div>
      <div className={`${style.row} p-fluid${disabled ? '' : ' p-subtheme-contrast'}`}>
        <Checkbox
          data-testid="stat-crosstab-mean"
          value="mean"
          checked={showMean}
          disabled={disabled}
          onChange={disabled ? null : () => setShowMean(!showMean)}
          onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setShowMean(!showMean))}
        />
        <span
          data-testid="stat-crosstab-mean-label"
          className={classNames(style['row-label'], { 'p-disabled': disabled })}
          onClick={disabled ? null : () => setShowMean(!showMean)}
        >
          Mean
        </span>
      </div>
    </div>
  )

  return (
    <div className={style.component}>
      <div className={style['component-title']}>Banner Tables</div>

      {renderBannersGroup()}
      {renderDataFormat()}
      {renderSignificanceTesting()}
      {renderSummaryMetrics()}

      <div className={style.section}>
        <div className={`${style.row} p-fluid`}>
          <Button
            className="p-button-stat p-button-rounded"
            label="Run and download"
            loading={processing}
            disabled={exportDisabled}
            onClick={exportDisabled ? null : onClick}
          />
        </div>

        {failed ? (
          <div className={`${style.row} ${style['row-error']} p-fluid`}>
            An error occurred while performing selected analysis operation. If the problem is reproducible please
            contact Support team.
          </div>
        ) : overLimit ? (
          <div className={`${style.row} ${style['row-error']} p-fluid`}>
            Please reduce the number of banners selected in order to run and download banner tables.
          </div>
        ) : responses < unlockNumber ? (
          <div className={`${style.row} ${style['row-error']} p-fluid`}>
            You need at least {unlockNumber} {plural(unlockNumber, 'respondent', 'respondents')} to create banner
            tables. Current filter settings show {responses} {plural(responses, 'respondent', 'respondents')}. Remove
            some filters or order more respondents to proceed.
          </div>
        ) : totalRespondents < unlockNumber ? (
          <div className={`${style.row} ${style['row-error']} p-fluid`}>
            You need at least {unlockNumber} {plural(unlockNumber, 'respondent', 'respondents')} to create banner
            tables. This survey currently contains only {totalRespondents}{' '}
            {plural(totalRespondents, 'complete', 'completes')}.
          </div>
        ) : !questions.length ? (
          <div className={`${style.row} ${style['row-error']} p-fluid`}>
            You need at least 1 question in order to run and download banner tables.
          </div>
        ) : null}
      </div>
    </div>
  )
}

CrosstabPanel.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onBannersSelect: PropTypes.func,
  onLevelsChange: PropTypes.func,
  onPercentDecimalsChange: PropTypes.func,
  onExport: PropTypes.func
}

CrosstabPanel.displayName = 'CrosstabPanel'

export default CrosstabPanel
